<template>
  <div class="dropdown-container" ref="dropdownRef">
    <div class="dropdown">
      <div class="dropdown-selected" @click="toggleDropdown">
        <font-awesome-icon :icon="currentOption.icon" />
        <span>{{ currentOption.label }}</span>
        <span class="dropdown-arrow" :class="{ open: isOpen }">▾</span>
      </div>

      <transition name="dropdown-fade">
        <ul v-if="isOpen" class="dropdown-options">
          <li
            v-for="option in lists"
            :key="option.id"
            class="dropdown-option"
            @click="selectOption(option.id)"
          >
            <font-awesome-icon :icon="option.icon" />
            <span>{{ option.label }}</span>
          </li>
        </ul>
      </transition>
    </div>
  </div>
</template>


<script lang="ts" setup>
import { ref, computed, defineEmits, defineProps, onMounted, onBeforeUnmount } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSeedling, faBullseye, faBoxArchive, faGlassWater, faBan } from '@fortawesome/free-solid-svg-icons';

// ✅ Define props
const props = defineProps<{
  modelValue?: string;
}>();

const emit = defineEmits<{
  (e: 'list-change', id: string): void;
  (e: 'update:modelValue', id: string): void; // Optional if you want v-model support
}>();

const dropdownRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const lists = [
  { id: 'current', label: 'Current Exposure', icon: faSeedling },
  { id: 'goals', label: 'Target Dishes', icon: faBullseye },
  { id: 'archived', label: 'Archived Dishes', icon: faBoxArchive },
  { id: 'safe', label: 'Safe Foods', icon: faGlassWater },
  { id: 'no-go', label: 'No-Go List', icon: faBan },
];

// ✅ Initialize selection with prop or fallback
const selectedValue = ref<string>(''); // Start blank, set in onMounted

const currentOption = computed(() => {
  return lists.find((l) => l.id === selectedValue.value) ?? lists[0];
});

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function selectOption(id: string) {
  selectedValue.value = id;
  emit('list-change', id);
  emit('update:modelValue', id); // Optional if using v-model
  isOpen.value = false;
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);

  // ✅ Initialize with prop if valid, else fallback
  if (props.modelValue && lists.some(l => l.id === props.modelValue)) {
    selectedValue.value = props.modelValue;
  } else {
    selectedValue.value = lists[0].id;
    emit('list-change', selectedValue.value); // Ensure parent knows the fallback
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>


<style scoped>
.dropdown-container {
  position: relative;
  width: 100%;
  font-family: sans-serif;
}

.dropdown-label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: bold;
}

.dropdown {
  position: relative;
}

.dropdown-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: none;
  border-radius: 0.375rem;
  background: #f2efeb;
  cursor: pointer;
  color: #a6917b;
  padding: 13px;
}

.dropdown-arrow {
  margin-left: auto;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: #f2efeb;
  border: none;
  border-radius: 0.375rem;
  margin-top: 0.25rem;
  list-style: none;
  padding: 0.25rem;
  color: #a6917b;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.dropdown-option:hover {
  background-color: #ece2d4;
}

/* Dropdown fade-slide transition */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
